import { Injectable, EventEmitter } from '@angular/core';
import { UserProvider } from '../user/user';
import * as debug from 'debug';
import * as _ from 'lodash';
import { App } from 'ionic-angular';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ToolsProvider } from '../tools/tools';

declare let CmKit: any;

interface HintsConfigs {
  app: any; // AppController
}

interface Hint {
  selector: string;
  text: string;
  forceCircle?: boolean;
  offset?: {
    x?: number;
    y?: number;
  };
  gatherBox?: Function;
  arrow: {
    vertical: 'top' | 'bottom' | 'center';
    horizontal: 'left' | 'center' | 'right';
  };
}

interface HintOpts {
  id: String;
}

interface WorkflowOpts {}

interface WorkflowRunOpts {
  delay?: number;
  quickDelay?: number;
}

interface Workflow {
  onVisitDelay: (
    visit: number,
    delay: number,
    hints: Hint[],
    validator?: Function
  ) => Workflow;
  onVisit: (visit: number, hints: Hint[], validator?: Function) => Workflow;
  run: Function;
}

// This boolean will be set to false with a 5s delay since we don't want to
// start two hints in code within 5s. First man wins if two in the same
// page or whatever
let canHint = true;

@Injectable()
export class HintsProvider {
  debug = debug('debug:app:HintsProvider');
  log = debug('app:HintsProvider');

  // event emitter to fire the tab bar hints
  tabBarReady = new EventEmitter();

  configs: HintsConfigs;
  activeWorkflow = false;
  timeoutDelays = [];

  constructor(
    public user: UserProvider,
    public crud: CrudApiProvider,
    public tools: ToolsProvider
  ) {
    this.tools.on('viewWillEnter', () => {
      this.debug('clearing delays');
      _.each(this.timeoutDelays, d => {
        clearTimeout(d);
      });
      this.timeoutDelays = [];
    });
  }

  configure(configs: HintsConfigs) {
    this.configs = configs;
    this.debug('configured', this.configs);
  }

  hintWorkflow(id: string, opts?: WorkflowOpts): Workflow {
    let minVisit = Infinity,
      maxVisit = 0;
    const visits = {},
      workflow: Workflow = {
        onVisitDelay: (
          visit: number,
          delay: number,
          hints: Hint[],
          validator?: Function
        ) => {
          minVisit = Math.min(minVisit, visit);
          maxVisit = Math.max(maxVisit, visit);
          visits[visit] = {
            hints: hints,
            validator: () => {
              return (
                timeValidator(delay) && (validator ? validator() : d => true)
              );
            },
          };
          return workflow;
        },
        onVisit: (visit: number, hints: Hint[], validator?: Function) => {
          minVisit = Math.min(minVisit, visit);
          maxVisit = Math.max(maxVisit, visit);
          visits[visit] = {
            hints: hints,
            validator: validator
              ? validator
              : () => {
                  return true;
                },
          };
          return workflow;
        },
        run: (
          runOptions: WorkflowRunOpts = { delay: 500, quickDelay: 50 },
          cb?: Function
        ) => {
          this.debug('starting workflow', id);

          setTimeout(() => {
            this.debug('starting workflow post quick delay', id);
            this.timeoutDelays.push(
              setTimeout(() => {
                this.debug('workflow post delay', workflow);
                if (!canHint || this.activeWorkflow) return;
                if (this.user.get('_id')) {
                  this.debug('check workflow', id, workflow);
                  const userValue = this.user.get(`metadata.hints.${id}`) || {
                    count: -1,
                    last: new Date(),
                  };

                  // don't want to infinitely incrememt for now reason
                  if (userValue.count + 1 <= maxVisit) {
                    this.debug(
                      'user value originally',
                      userValue.count,
                      userValue.last
                    );

                    userValue.count++;
                    userValue.last = new Date();
                    this.debug('user value now', userValue);
                    this.user.set(`metadata.hints.${id}`, userValue);

                    if (visits[userValue.count]) {
                      try {
                        if (visits[userValue.count].validator()) {
                          this.showHints(
                            visits[userValue.count].hints,
                            cb && cb
                          );
                        }
                      } catch (e) {}
                    }

                    if (!localStorage.getItem('TEST_HINTS')) {
                      this.crud
                        .put(
                          `/api/users/${this.user.get('_id')}/metadata/hints`,
                          {
                            key: id,
                            value: userValue,
                          }
                        )
                        .then(
                          () => {},
                          () => {}
                        );
                    }
                  } else {
                    this.debug(
                      'count to high (%s vs %s)',
                      userValue.count,
                      maxVisit
                    );
                  }
                }
              }, runOptions.delay - runOptions.quickDelay)
            );
          }, runOptions.quickDelay);
        },
      };

    function timeValidator(delay: number) {
      // TODO
      this.debug('time validator');
      return true;
    }

    return workflow;
  }

  // showHints(opts: HintOpts, hints: Hint[],) {
  private showHints(hints: Hint[], cb?: Function) {
    canHint = false;
    setTimeout(() => {
      canHint = true;
    }, 5e3);

    this.debug('show hints');

    hints = hints.filter(h => {
      return this._queryContainer(h.selector);
    });

    if (_.size(hints)) {
      try {
        if (document.activeElement) {
          let da = <any>document.activeElement;
          da.blur();
        }
      } catch (e) {}
    }

    let index = 0;
    this.activeWorkflow = true;
    const run = () => {
      const h = hints[index],
        next = hints[index + 1];

      if (!h) {
        this.activeWorkflow = false;
        setTimeout(() => {
          cb && cb();
        }, 1e3);
        return;
      }

      this._createHint(
        h,
        {
          step: index + 1,
          total: hints.length,
          hasNext: !!next,
        },
        () => {
          index++;
          run();
        }
      );
    };

    run();
  }

  private _createHint(
    hint: Hint,
    opts: { step: Number; total: Number; hasNext: boolean },
    onNext?: Function
  ) {
    const parentElement = this._queryContainer(hint.selector);

    if (!parentElement) {
      this.debug("Can't find parent");
      return;
    }

    this.debug('hint parent', parentElement);
    const boundingBox = hint.gatherBox
      ? hint.gatherBox(parentElement)
      : parentElement.getBoundingClientRect();

    if (hint.offset && hint.offset.x) {
      boundingBox.x += hint.offset.x;
    } else if (hint.offset && hint.offset.y) {
      boundingBox.y += hint.offset.y;
    }

    this.debug('parent box', boundingBox);

    const render = CmKit.utils.template`
    <div class="cm-HintOverlay"></div>
    <div class="cm-HintSpotlight"
         style="${this._spotlightSizing(boundingBox, hint)}"></div>
    <div class="cm-Hint"
        style="width: 200px; position: absolute; right: 0px; bottom: 0px;"
        style="${this._hintSizing(boundingBox, hint)}"
        cm-color="Primary / CDLLife Blue">
      <div class="cm-Hint-arrow
                  cm-Hint-arrow--v-${hint.arrow.vertical}
                  cm-Hint-arrow--${hint.arrow.horizontal}"></div>
      ${hint.text}
      <br  style="display: ${opts.hasNext ? 'inline-block' : 'none'}" />
      <br  style="display: ${opts.hasNext ? 'inline-block' : 'none'}" />
      <div>
        <a class="cm-Hint-info" cm-font="Body 16pt / Regular"
          style="display: ${opts.total > 1 ? 'inline-block' : 'none'}">
          Step ${opts.step} of ${opts.total}
        </a>
        <a class="cm-Hint-step" cm-font="Button 12pt / Medium"
          style="display: 'inline-block'">
          ${opts.hasNext ? 'Next' : 'Got It'}
        </a>
      </div>
      <br  style="display: ${opts.hasNext ? 'inline-block' : 'none'}" />
    </div>
      `,
      div = document.createElement('div');

    div.classList.add('HintParent');
    div.addEventListener('click', () => {
      div.remove();
      onNext && onNext();
    });
    render(div);
    document.querySelector('ion-app').appendChild(div);
  }

  private _spotlightSizing(box, hint: Hint) {
    let { height, width, x, y } = box;
    let radius = '50%';

    // close to circle
    if (hint.forceCircle || (height / width > 0.6 && height / width < 1.4)) {
      height = width = Math.max(height, width);
    } else {
      radius = '50px';
    }

    x = x + width / 2;
    y = y + height / 2;

    return `
      top: ${(box.y + box.y + box.height) / 2}px;
      left: ${(box.x + box.x + box.width) / 2}px;
      transform: translate(-50%, -50%);
      border-radius: ${radius};
      height: ${height}px;
      width: ${width}px;
    `;
  }

  private _hintSizing(box, hint: Hint) {
    let str = `position: fixed;`;

    if (hint.arrow.horizontal === 'center') {
      str += `width: ${Math.max(box.width, 200)}px;`;
    } else {
      str += `width: 200px;`;
    }

    if (hint.arrow.vertical === 'top') {
      str += `top: ${box.y + box.height + 18}px;`;
    } else if (hint.arrow.vertical === 'bottom') {
      str += `bottom: ${window.innerHeight - box.y + 18}px;`;
    } else if (hint.arrow.vertical === 'center') {
      str += `top: ${box.y + box.height / 2}px;`;
      str += `transform: translateY(-50%);`;
    }

    if (hint.arrow.vertical === 'center') {
      // If vertically centered, we want the thing left of the content or right
      // of the content, not centered horizontally where the arrow is
      if (hint.arrow.horizontal === 'left') {
        str += `left: ${box.x + box.width + 18}px;`;
      } else if (hint.arrow.horizontal === 'right') {
        str += `right: ${window.innerWidth - (box.x - 18)}px;`;
      }
    } else {
      if (hint.arrow.horizontal === 'left') {
        str += `left: ${box.x + box.width / 2 - 18}px;`;
      } else if (hint.arrow.horizontal === 'right') {
        str += `right: ${window.innerWidth - (box.x + box.width / 2 + 18)}px;`;
      } else if (hint.arrow.horizontal === 'center') {
        str += `left: ${box.x + box.width / 2}px;`;
        str += `transform: translateX(-50%);`;
      }
    }

    return str;
  }

  private _queryContainer(query) {
    try {
      const nav = this.configs.app.appCtrl.getActiveNav();
      let recentView;

      this.debug('nav', nav);
      if (nav._tabs) {
        this.debug('tabs', nav, nav._tabs);
        const activeTab = _.find(nav._tabs, d => d.isSelected) || nav._tabs[0];
        recentView = _.find(
          (activeTab._views || []).slice().reverse(),
          d => !d._isHidden && !d._isHidden
        );
      } else {
        this.debug('views', nav._views);
        recentView = _.find(
          (nav._views || []).slice().reverse(),
          d => !d._isHidden && !d._isHidden
        );
      }

      this.debug('recentView', recentView);
      const page = recentView._cmp.location.nativeElement;
      this.debug('parent', page);
      return (
        page.querySelector(query) ||
        document.querySelector('.tabbar').querySelector(query)
      );
    } catch (e) {
      this.debug('error finding parent', e);
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/hints/hints.ts